import { useState, useEffect, useRef, memo } from "react";
import type { TurnPhase } from "../hooks/useChat";

interface ThinkingPulseProps {
  turnPhase: TurnPhase;
  turnStartedAt: number | null;
  subprocessAlive: boolean;
}

function formatElapsed(ms: number): string {
  const totalSec = Math.floor(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${String(sec).padStart(2, "0")}`;
}

function phaseLabel(phase: TurnPhase): string {
  if (!phase) return "";
  switch (phase.phase) {
    case "submitted": return "thinking";
    case "thinking":  return "thinking";
    case "tool_use":  return humanizeToolName(phase.toolName);
    case "streaming": return "writing";
  }
}

function humanizeToolName(toolName: string): string {
  const match = toolName.match(/^mcp__(.+?)__(.+)$/);
  const base = match ? match[2] : toolName;
  return base.replace(/_/g, " ");
}

export const ThinkingPulse = memo(function ThinkingPulse({
  turnPhase,
  turnStartedAt,
  subprocessAlive,
}: ThinkingPulseProps) {
  const [elapsed, setElapsed] = useState(0);
  const [label, setLabel] = useState("");
  const [labelVisible, setLabelVisible] = useState(true);
  const prevLabelRef = useRef("");

  useEffect(() => {
    if (!turnStartedAt) {
      setElapsed(0);
      return;
    }
    setElapsed(Date.now() - turnStartedAt);
    const timer = setInterval(() => {
      setElapsed(Date.now() - turnStartedAt);
    }, 1000);
    return () => clearInterval(timer);
  }, [turnStartedAt]);

  useEffect(() => {
    const newLabel = phaseLabel(turnPhase);
    if (newLabel === prevLabelRef.current) return;
    setLabelVisible(false);
    const timeout = setTimeout(() => {
      prevLabelRef.current = newLabel;
      setLabel(newLabel);
      setLabelVisible(true);
    }, 200);
    return () => clearTimeout(timeout);
  }, [turnPhase]);

  return (
    <div className="thinking-indicator" role="status" aria-live="polite">
      <span
        className={`thinking-pulse-dot${!subprocessAlive ? " dead" : ""}`}
        aria-hidden="true"
      />
      <span className="thinking-label" style={{ opacity: labelVisible ? 1 : 0 }}>
        {label}
      </span>
      {turnStartedAt && elapsed >= 1000 && (
        <span
          className="thinking-timer"
          aria-label={`Elapsed: ${formatElapsed(elapsed)}`}
        >
          · {formatElapsed(elapsed)}
        </span>
      )}
    </div>
  );
});
